import { useState, useMemo } from "react";
import { formatReportingPeriod, getReportingPeriodLabel } from "./formatReportingPeriod";

export { formatReportingPeriod, getReportingPeriodLabel };

const getScorecardPeriod = (scorecard) =>
  formatReportingPeriod(scorecard.reportingPeriodStart, scorecard.reportingFrequency);

/**
 * Custom hook that manages the cascading filter state for scorecards:
 *   Reporting Period -> Company -> Report (scorecard)
 *
 * Accepts the full list of scorecards loaded for the current rubric and
 * returns derived dropdown options plus selection state and handlers.
 */
const useScorecardFilters = (allScorecards) => {
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [selectedScorecardId, setSelectedScorecardId] = useState("");

  const availablePeriods = useMemo(() => {
    const periodsSet = new Set();
    allScorecards.forEach((sc) => {
      const period = getScorecardPeriod(sc);
      if (period) periodsSet.add(period);
    });
    return Array.from(periodsSet).sort((a, b) => b.localeCompare(a));
  }, [allScorecards]);

  const scorecardsByPeriod = useMemo(() => {
    if (!selectedPeriod) return allScorecards;
    return allScorecards.filter((sc) => getScorecardPeriod(sc) === selectedPeriod);
  }, [allScorecards, selectedPeriod]);

  const availableCompanies = useMemo(() => {
    const companiesMap = {};
    scorecardsByPeriod.forEach((sc) => {
      if (sc.company?.id) {
        companiesMap[sc.company.id] = sc.company.name || sc.company.id;
      }
    });
    return Object.entries(companiesMap)
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [scorecardsByPeriod]);

  const scorecardsByCompany = useMemo(() => {
    if (!selectedCompanyId) return scorecardsByPeriod;
    return scorecardsByPeriod.filter((sc) => sc.company?.id === selectedCompanyId);
  }, [scorecardsByPeriod, selectedCompanyId]);

  const availableReports = useMemo(() => {
    return scorecardsByCompany.map((sc) => {
      const periodLabel = getScorecardPeriod(sc) || sc.reportId;
      return {
        scorecardId: sc.id,
        companyName: sc.company?.name || "",
        reportId: sc.reportId,
        reportingFrequency: sc.reportingFrequency || "",
        reportingPeriodStart: sc.reportingPeriodStart || "",
        displayName: `${sc.company?.name || "Unknown"} — ${sc.reportingFrequency || "Report"} (${periodLabel})`,
      };
    });
  }, [scorecardsByCompany]);

  const handlePeriodSelect = (period) => {
    setSelectedPeriod(period);
    setSelectedCompanyId("");
    setSelectedScorecardId("");
  };

  const handleCompanySelect = (companyId) => {
    setSelectedCompanyId(companyId);
    setSelectedScorecardId("");
  };

  const resetFilters = () => {
    setSelectedPeriod("");
    setSelectedCompanyId("");
    setSelectedScorecardId("");
  };

  return {
    selectedPeriod,
    selectedCompanyId,
    selectedScorecardId,
    setSelectedScorecardId,
    availablePeriods,
    availableCompanies,
    availableReports,
    handlePeriodSelect,
    handleCompanySelect,
    resetFilters,
  };
};

export default useScorecardFilters;
